import { DocumentInfoCard } from '../InfoCard/types';
import { format, parse, parseISO } from 'date-fns';
import {
	DocumentValueMutatorArgs,
	DocumentsMutatorArgs,
	DocumentType,
	MutatorArgs,
	TravellerDocumentFields,
	TravellerFields,
	TravellerFormType,
	ValueMutatorArgs,
	DropdownOption,
	DirectionsMutatorArgs
} from './types';
import { InputType } from '../Passenger/types';
import { MutableState, Tools } from 'final-form';
import { initialTravellerDocument } from './consts';
import { ProfileDocumentFragment, TravellerFieldEnum } from '@websky/graphql';
import { getUserValue } from '../utils';

export const convertDocuments = (documentsList: ProfileDocumentFragment[]): DocumentInfoCard[] => {
	return documentsList.map(document => {
		const firstName = getUserValue(document, TravellerFieldEnum.FirstName);
		const lastName = getUserValue(document, TravellerFieldEnum.LastName);
		const middleName = getUserValue(document, TravellerFieldEnum.MiddleName);
		const docNumber = getUserValue(document, TravellerFieldEnum.DocNumber);
		const docType = getUserValue(document, TravellerFieldEnum.DocType);
		const options = document.values
			.find(value => value.name === 'docType')
			.validationRules[0].options.map(option => option.value);

		let docExpiryDate = null;
		const docExpiryDateField = getUserValue(document, TravellerFieldEnum.DocExpiryDate);

		if (docExpiryDateField) {
			try {
				docExpiryDate = format(parseISO(docExpiryDateField), 'dd.MM.yyyy');
			} catch (e) {
				try {
					docExpiryDate = format(parse(docExpiryDateField, 'dd.MM.yyyy', new Date()), 'dd.mm.yyyy');
				} catch (e) {
					docExpiryDate = format(parseISO(docExpiryDateField), 'dd-MM-yyyy');
				}
			}
		}

		const citizenship = getUserValue(document, TravellerFieldEnum.Citizenship);

		return {
			firstName,
			lastName,
			middleName,
			docNumber,
			docType,
			docExpiryDate,
			citizenship,
			options
		};
	});
};

export const getInputType = (fieldName: string): InputType => {
	if (fieldName === TravellerFields.BirthDate) {
		return InputType.Date;
	}

	if (fieldName === TravellerFields.Gender) {
		return InputType.Switch;
	}

	if (fieldName === TravellerDocumentFields.Nationality) {
		return InputType.Switch;
	}

	if (fieldName === TravellerFields.Phone) {
		return InputType.Phone;
	}

	return InputType.Text;
};

export const documentsMutator = (
	params: DocumentsMutatorArgs[],
	state: MutableState<TravellerFormType>,
	utils: Tools<TravellerFormType>
) => {
	const documents: DocumentType[] = state.formState.values[TravellerFields.Documents];

	if (params[0].type === 'add') {
		utils.changeValue(state, TravellerFields.Documents, () => [
			...state.formState.values[TravellerFields.Documents],
			initialTravellerDocument
		]);
	} else if (params[0].type === 'remove') {
		const updatedDocuments = documents.filter((_, index) => index !== params[0].index);

		utils.changeValue(state, TravellerFields.Documents, () => updatedDocuments);
	}
};

export const documentMutator = (
	params: DocumentValueMutatorArgs[],
	state: MutableState<TravellerFormType>,
	utils: Tools<TravellerFormType>
) => {
	const firstDocument: DocumentType = state.formState.values[TravellerFields.Documents][0];

	const fieldName = params[0].fieldName;
	utils.changeValue(state, fieldName, () => firstDocument[fieldName]);
};

export const docTypeMutator = (
	params: MutatorArgs[],
	state: MutableState<TravellerFormType>,
	utils: Tools<TravellerFormType>
) => {
	const documents: DocumentType[] = state.formState.values[TravellerFields.Documents];

	utils.changeValue(state, TravellerFields.Documents, () =>
		documents.map((document, index) => {
			if (index === params[0].index) {
				return {
					...document,
					docExpiryDate: ''
				};
			}

			return document;
		})
	);
};

export const citizenshipMutator = (
	params: MutatorArgs[],
	state: MutableState<TravellerFormType>,
	utils: Tools<TravellerFormType>
) => {
	const documents: DocumentType[] = state.formState.values[TravellerFields.Documents];

	utils.changeValue(state, TravellerFields.Documents, () =>
		documents.map((document, index) => {
			if (index === params[0].index) {
				return {
					...document,
					docType: ''
				};
			}

			return document;
		})
	);

	utils.changeValue(state, TravellerFields.Nationality, () => documents[0]?.citizenship);
};

export const directionsMutator = (
	[fieldName, option, action]: DirectionsMutatorArgs,
	state: MutableState<TravellerFormType>,
	utils: Tools<TravellerFormType>
) => {
	utils.changeValue(state, fieldName, (oldValue: DropdownOption[]) => {
		const newValue = oldValue || [];

		if (action === 'add') {
			return [...newValue, option];
		}

		if (action === 'delete') {
			return newValue.filter(value => value.value !== option.value);
		}

		return newValue;
	});
};

export const fixEmptyFieldMutator = (
	params: ValueMutatorArgs[],
	state: MutableState<TravellerFormType>,
	utils: Tools<TravellerFormType>
) => {
	const fieldName = params[0].fieldName;

	if (!(fieldName in state.formState.values)) {
		utils.changeValue(state, fieldName, () => (state.formState.values[fieldName] = ''));
	}
};

export const fixEmptyFieldDocumentMutator = (
	params: DocumentValueMutatorArgs[],
	state: MutableState<TravellerFormType>,
	utils: Tools<TravellerFormType>
) => {
	const doc: DocumentType = state.formState.values[TravellerFields.Documents][params[0].index];

	const fieldName = params[0].fieldName;
	if (!(fieldName in doc)) {
		utils.changeValue(state, fieldName, () => (doc[fieldName] = ''));
	}
};
